import { useCallback, useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { useMutation, useQuery } from '@connectrpc/connect-query'
import { useTranslation } from 'react-i18next'

import { listContracts } from '@enechain/eposition-proto/bffcommon/v1/eex_service-EEXService_connectquery'
import { simulateOrders } from '@enechain/eposition-proto/bffportal/v1/simulation_service-SimulationService_connectquery'
import { SimulateOrdersRequestSchema } from '@enechain/eposition-proto/bffportal/v1/simulation_service_pb'
import {
  OrderSimulationRelationWithNumberSchema,
  OrderWithNumber,
  OrderWithNumberSchema,
} from '@enechain/eposition-proto/devtool/v1/service_pb'
import { OrderSchema } from '@enechain/eposition-proto/external/v1/model_pb'
import { ECLEAR_OPTION_CATEGORY } from '@enechain/eposition-ui/src/models/eclearOption'
import { getErrorMessage } from '@enechain/eposition-utils'

import {
  MOCK_PORTFOLIO_ID,
  convertLegsFromOrderPatternModel,
  getSpreadType,
} from '~/pages/Simulation/models/SimulationOrderPattern'
import { convertToSimulationOrderPatternModels } from '~/pages/Simulation/models/SimulationOrderPatterns'
import { convertMarginSimulationResultForOption1Models } from '~/pages/Simulation/models/SimulationResultModel/MarginSimulationResultForOption1Models'
import { convertMarginSimulationResultForOption2Models } from '~/pages/Simulation/models/SimulationResultModel/MarginSimulationResultForOption2Models'
import { MarginSimulationResultModel } from '~/pages/Simulation/models/SimulationResultModel/MarginSimulationResultModel'
import {
  ResultSimulationForOption1Model,
  convertResultSimulationForOption1Model,
} from '~/pages/Simulation/models/SimulationResultModel/ResultSimulationForOption1Model'
import {
  ResultSimulationForOption2Model,
  convertResultSimulationForOption2Model,
} from '~/pages/Simulation/models/SimulationResultModel/ResultSimulationForOption2Model'
import { OrderSimulationSchema } from '~/pages/Simulation/schemas'
import { useGetMe } from '~/pages/hooks/useGetMe'

type Result = {
  invokeSimulateMargin: (props: {
    value: OrderSimulationSchema
    onSuccess: () => void
    onError: (error: string) => void
  }) => Promise<void>
  progress: number
  result: MarginSimulationResultModel
  isProcessing: boolean
}

type MutateFn = (props: {
  value: OrderSimulationSchema
  onSuccess: () => void
  onError: (error: string) => void
}) => Promise<void>

export function useSimulateMargin(): Result {
  const mutation = useMutation(simulateOrders)
  const [progress, setProgress] = useState<number>(0)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [result, setResult] = useState<MarginSimulationResultModel>(undefined)
  const { t } = useTranslation(['common'])
  const { optionCategory } = useGetMe()
  const { data: eexContractData } = useQuery(
    listContracts,
    {},
    { throwOnError: false },
  )
  function resetBeforeSimulation(): void {
    setResult(undefined)
    setProgress(0)
    setIsProcessing(true)
  }

  function failSimulation(): void {
    setResult(undefined)
    setProgress(0)
    setIsProcessing(false)
  }

  const invokeSimulateMargin: MutateFn = useCallback(
    async ({ value, onSuccess, onError }) => {
      resetBeforeSimulation()

      const eexContracts = eexContractData?.contracts ?? []

      const { orderPatternModels, error } =
        convertToSimulationOrderPatternModels(value, 'MARGIN', eexContracts)
      if (error !== undefined) {
        onError(error)
        failSimulation()
        return
      }
      const orderPattenRequests: OrderWithNumber[] =
        orderPatternModels?.models.map((order) =>
          create(OrderWithNumberSchema, {
            orderNumber: order.orderNumber,
            order: create(OrderSchema, {
              platformId: MOCK_PORTFOLIO_ID,
              productSpreadType: getSpreadType(order),
              legs: convertLegsFromOrderPatternModel(order),
            }),
          }),
        ) ?? []

      const requestParamsArray =
        value.marginSimulationRelation.length > 0
          ? value.marginSimulationRelation.map((relation, index) =>
              create(SimulateOrdersRequestSchema, {
                ...value,
                orders: orderPattenRequests,
                simulationRelations: [
                  create(OrderSimulationRelationWithNumberSchema, {
                    relationNumber: index + 1,
                    orderNumbers: relation.values,
                  }),
                ],
                withUnitPriceBuffer: true,
                caseId: '',
              }),
            )
          : [
              create(SimulateOrdersRequestSchema, {
                ...value,
                orders: [],
                simulationRelations: [
                  create(OrderSimulationRelationWithNumberSchema, {
                    relationNumber: 1,
                    orderNumbers: [],
                  }),
                ],
                withUnitPriceBuffer: true,
                caseId: '',
              }),
            ]
      const detailsForOption1: ResultSimulationForOption1Model[] = []
      const detailsForOption2: ResultSimulationForOption2Model[] = []

      try {
        for (const [index, requestParams] of requestParamsArray.entries()) {
          const response = await mutation.mutateAsync(requestParams)

          if (optionCategory === ECLEAR_OPTION_CATEGORY.OPTION_1) {
            detailsForOption1.push(
              ...convertResultSimulationForOption1Model(response),
            )
          } else if (optionCategory === ECLEAR_OPTION_CATEGORY.OPTION_2) {
            detailsForOption2.push(
              ...convertResultSimulationForOption2Model(response),
            )
          }

          setProgress(((index + 1) / requestParamsArray.length) * 100)
        }

        if (optionCategory === ECLEAR_OPTION_CATEGORY.OPTION_1) {
          setResult(
            convertMarginSimulationResultForOption1Models(detailsForOption1),
          )
        } else if (optionCategory === ECLEAR_OPTION_CATEGORY.OPTION_2) {
          setResult(
            convertMarginSimulationResultForOption2Models(detailsForOption2),
          )
        }
        onSuccess()
      } catch (error) {
        setResult(undefined)
        if (error instanceof Error) {
          const errorMessage = getErrorMessage({ error })
          onError(errorMessage)
          failSimulation()
        } else {
          onError(t('common:error.error_occurred'))
        }
      } finally {
        setIsProcessing(false)
      }
    },
    [mutation, t, optionCategory, eexContractData?.contracts],
  )

  return {
    invokeSimulateMargin,
    progress,
    result,
    isProcessing,
  }
}
